import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { BarChart3, GripVertical, ChevronUp, ChevronDown, RotateCcw } from 'lucide-react';
import {
  PLAYER_STAT_FIELDS,
  DEFAULT_PLAYER_CARD_STATS,
  MAX_PLAYER_CARD_STATS,
} from '../../lib/playerStatFields';

/**
 * Lets the coach pick which stats appear on each player card (Players page)
 * and what order they appear in. Hard-capped at MAX_PLAYER_CARD_STATS.
 *
 * State shape: ordered array of stat keys, persisted to
 * `user.player_card_stats` via PUT /api/settings.
 */
export function PlayerCardStatsSection({ playerCardStats, setPlayerCardStats }) {
  const selection = Array.isArray(playerCardStats) && playerCardStats.length > 0
    ? playerCardStats
    : DEFAULT_PLAYER_CARD_STATS;
  const isAtMax = selection.length >= MAX_PLAYER_CARD_STATS;

  const toggle = (key) => {
    if (selection.includes(key)) {
      setPlayerCardStats(selection.filter((k) => k !== key));
    } else if (!isAtMax) {
      setPlayerCardStats([...selection, key]);
    }
  };

  const move = (key, dir) => {
    const idx = selection.indexOf(key);
    if (idx < 0) return;
    const next = [...selection];
    const swapWith = idx + dir;
    if (swapWith < 0 || swapWith >= next.length) return;
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
    setPlayerCardStats(next);
  };

  const resetToDefaults = () => setPlayerCardStats([...DEFAULT_PLAYER_CARD_STATS]);

  // Picked rows in their chosen order, plus unpicked alphabetised below.
  const pickedRows = selection
    .map((k) => PLAYER_STAT_FIELDS.find((f) => f.key === k))
    .filter(Boolean);
  const unpicked = PLAYER_STAT_FIELDS
    .filter((f) => !selection.includes(f.key))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Card data-testid="player-card-stats-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-emerald-600" />
          Player Card Stats
        </CardTitle>
        <CardDescription>
          Choose which stats appear on each player card on the Players page — up to {MAX_PLAYER_CARD_STATS}. Use the arrows to set the order.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span data-testid="player-card-stats-count">
            <strong className="text-gray-700">{selection.length}</strong> selected · {MAX_PLAYER_CARD_STATS - selection.length} slots left
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={resetToDefaults}
            data-testid="player-card-stats-reset"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to defaults
          </Button>
        </div>

        {/* Picked, ordered */}
        {pickedRows.length > 0 && (
          <div className="border rounded-xl divide-y bg-emerald-50/40">
            <div className="px-3 py-2 text-[11px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50">
              Showing on cards (in this order)
            </div>
            {pickedRows.map((f, idx) => (
              <div key={f.key} className="flex items-center gap-2 px-3 py-2" data-testid={`player-stat-row-${f.key}`}>
                <GripVertical className="w-4 h-4 text-gray-300" />
                <span className="text-xs font-mono text-gray-400 w-5">{idx + 1}.</span>
                <span className={`font-medium flex-1 ${f.color}`}>{f.label}</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => move(f.key, -1)}
                    disabled={idx === 0}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label={`Move ${f.label} up`}
                    data-testid={`player-stat-up-${f.key}`}
                  >
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(f.key, 1)}
                    disabled={idx === pickedRows.length - 1}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label={`Move ${f.label} down`}
                    data-testid={`player-stat-down-${f.key}`}
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  <Checkbox
                    id={`player-stat-toggle-${f.key}`}
                    checked
                    onCheckedChange={() => toggle(f.key)}
                    data-testid={`player-stat-toggle-${f.key}`}
                    aria-label={`Hide ${f.label}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Unpicked */}
        {unpicked.length > 0 && (
          <div className="border rounded-xl divide-y">
            <div className="px-3 py-2 text-[11px] uppercase tracking-widest font-bold text-gray-500 bg-gray-50">
              Available stats {isAtMax && <span className="text-amber-600 normal-case font-medium">(max reached — uncheck one to add another)</span>}
            </div>
            {unpicked.map((f) => (
              <div key={f.key} className={`flex items-center gap-3 px-3 py-2 ${isAtMax ? 'opacity-50' : ''}`} data-testid={`player-stat-available-${f.key}`}>
                <Checkbox
                  id={`player-stat-toggle-${f.key}`}
                  checked={false}
                  onCheckedChange={() => toggle(f.key)}
                  disabled={isAtMax}
                  data-testid={`player-stat-toggle-${f.key}`}
                />
                <Label htmlFor={`player-stat-toggle-${f.key}`} className={`font-medium flex-1 cursor-pointer ${f.color}`}>{f.label}</Label>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PlayerCardStatsSection;
